import { Link, Route, Routes } from "react-router-dom";
import classNames from "./Home.module.css";
import SideNav from "./SideNav";
import useUser from "../../../hooks/user";
import Meetings from "./Meetings";
import Settings from "./settings/Settings";
import Billing, { Pricing } from "./Billing";
import Usage from "./Usage";
import { useOrganization } from "../../context/organization/organization";
import integrationImg from "../../../assets/stock/integration.webp";
import eventImg from "../../../assets/stock/event.webp";
import meetingImg from "../../../assets/stock/meeting.webp";
import {
  getLanguageFlag,
  getLanguageName,
  supportedLanguages,
  topLanguages,
} from "../../../types";
import Button from "../../common/Button";
import { useMemo } from "react";
import teamsImg from "../../../assets/platforms/teams.webp";
import meetImg from "../../../assets/platforms/meet.webp";
import zoomImg from "../../../assets/platforms/zoom.webp";
import Support from "./Support";
import { useIsMobile } from "../../../hooks/layout";

type HeroCardProps = {
  title: string;
  platformUrl: string;
  buttonText: string;
  platforms: string[];
  imageUrl: string;
};

function PlatformBadge(props: { platform: string }) {
  const content = useMemo(() => {
    switch (props.platform) {
      case "teams":
        return (
          <>
            <img src={teamsImg} alt="Teams" />
            Teams
          </>
        );
      case "meet":
        return (
          <>
            <img src={meetImg} alt="Google Meet" />
            Google Meet
          </>
        );
      case "zoom":
        return (
          <>
            <img src={zoomImg} alt="Zoom" />
            Zoom
          </>
        );
      case "langfinity":
        return "Langfinity";
    }
  }, [props.platform]);

  return (
    <div>
      {content}
      {props.platform === "meet" || props.platform === "zoom" ? (
        <label className={classNames.ComingSoon}>Coming soon</label>
      ) : null}
    </div>
  );
}

export const PlatformBadges = (props: { platforms: string[] }) => {
  return (
    <div className={classNames.PlatformBadges}>
      {props.platforms.map((platform, index) => (
        <PlatformBadge key={index} platform={platform} />
      ))}
    </div>
  );
};

function HeroCard({
  title,
  platformUrl,
  buttonText,
  platforms,
  imageUrl,
}: HeroCardProps) {
  return (
    <div className={classNames.HeroCard}>
      <img src={imageUrl} />
      <PlatformBadges platforms={platforms} />
      <h2 className={classNames.HeroCardTitle}>{title}</h2>
      <Link
        style={{ width: "100%" }}
        to={
          platformUrl === "bot"
            ? "https://appsource.microsoft.com/en-us/product/office/WA200007477"
            : `/create/${platformUrl}`
        }
        target={platformUrl === "bot" ? "_blank" : undefined}
      >
        <Button style={{ width: "100%" }}>{buttonText}</Button>
      </Link>
    </div>
  );
}

function Hero() {
  const cards: HeroCardProps[] = [
    {
      title: "Translation for Teams, Zoom, and Google Meet",
      buttonText: "Invite AI translator to online meetings",
      platforms: ["teams", "zoom", "meet"],
      platformUrl: "bot",
      imageUrl: integrationImg,
    },
    {
      title: "In-person and hybrid events or conferences",
      buttonText: "Schedule an event",
      platforms: ["langfinity"],
      platformUrl: "event",
      imageUrl: eventImg,
    },
    {
      title: "Online meetings with Langfinity",
      buttonText: "Get started",
      platforms: ["langfinity"],
      platformUrl: "meeting",
      imageUrl: meetingImg,
    },
  ];

  const [user] = useUser();

  return (
    <div className={classNames.HomePageContainer}>
      <h1>
        Welcome,{" "}
        {user.auth?.name.includes(" ")
          ? user.auth.name.split(" ")[0]
          : user.auth?.name}
        !
      </h1>
      <h2>
        Where would you like to host your multilingual meetings and events?
      </h2>
      <div className={classNames.CardContainer}>
        {cards.map((card, index) => (
          <HeroCard key={index} {...card} />
        ))}
      </div>
    </div>
  );
}

function SupportedLanguages() {
  return (
    <div className={classNames.SupportedLanguages}>
      <h2>Languages supported</h2>
      <div>
        {[
          ...Object.values(supportedLanguages)
            .flatMap((languages) => Array.from(languages))
            .filter((l) => topLanguages.includes(l))
            .sort((a, b) => topLanguages.indexOf(a) - topLanguages.indexOf(b)),
          ...Object.values(supportedLanguages)
            .flatMap((languages) => Array.from(languages))
            .filter((l) => !topLanguages.includes(l)),
        ].map((language) => (
          <div key={language} className={classNames.LanguageBadge}>
            <span>{getLanguageFlag(language)}</span>
            <span>{getLanguageName(language)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function HomeNavAndContent() {
  const { organization, loading } = useOrganization();
  const isMobile = useIsMobile();

  if (!organization || loading) {
    return null;
  }

  return (
    <>
      <SideNav />
      <div className={classNames.HomeContent}>
        <Routes>
          <Route
            path="/"
            element={
              <>
                <Hero />
                {/* Upcoming */}
                <Meetings hidePast={true} />
                {/* Languages supported */}
                {!isMobile && <SupportedLanguages />}
                {/* Pricing and plan */}
                <Pricing />
              </>
            }
          />
          <Route
            path="/meetings"
            element={
              <>
                <Meetings hidePast={false} />
              </>
            }
          />
          <Route
            path="/settings/*"
            element={
              <>
                <Settings />
              </>
            }
          />
          <Route
            path="/billing"
            element={
              <>
                <Billing />
              </>
            }
          />
          <Route
            path="/usage"
            element={
              <>
                <Usage />
              </>
            }
          />
          <Route
            path="/support"
            element={
              <>
                <Support />
              </>
            }
          />
        </Routes>
      </div>
    </>
  );
}

export default function Home() {
  return (
    <div className={classNames.Home}>
      <HomeNavAndContent />
    </div>
  );
}
